// facts/digest.ts - the coach-facing projection. ENGINE_DESIGN.md §8.1; ARCHITECTURE.md §5.
//
// `EngineFacts` is the FULL deterministic record (exact hole cards, exact equities, dollars, EV table).
// `FactsDigest` is the ONLY thing the LLM ever receives, and it carries NO hole cards, NO raw card
// strings, and NO raw inputs - bucketed conclusions only. Two things enforce that firewall here:
//
//   1. BY CONSTRUCTION - projectDigest reads only the bucketed/enumerated fields of EngineFacts; it
//      never reads `hero.holeCards` or `board.cards`. The FactsDigest TYPE has no field to hold them.
//   2. ENFORCED AT RUNTIME - assertDigestSafe scans the serialized digest for a card pattern
//      (`[2-9TJQKA][cdhs]`) or a `$` amount and THROWS. This catches the one residual leak the type
//      system can't: an authored free-form label (a rangeShape `topClasses` entry, a rule `principle`,
//      a `heroPerceived.summary` label) that accidentally embeds card-like text. projectDigest runs
//      this guard before returning, so a poisoned label fails loudly instead of reaching the coach.
import { FactsDigestSchema, } from '@postflop/schema';
// ── bucket functions (single source of truth, ENGINE_DESIGN.md §8.1) ─────────────
// Versioned WITH the digest schema. Boundaries are lower-inclusive / upper-exclusive except where the
// doc pins the split explicitly (equity >70 dominant ⇒ 70 is still 'ahead'; ±5 held ⇒ ±5 inclusive).
/** equity (0..1) → bucket. <25 weak | 25–40 behind | 40–55 flip | 55–70 ahead | >70 dominant. */
export function equityBucketOf(equity) {
    const pct = equity * 100;
    if (pct < 25)
        return 'weak';
    if (pct < 40)
        return 'behind';
    if (pct < 55)
        return 'flip';
    if (pct <= 70)
        return 'ahead';
    return 'dominant';
}
/** equity-delta in PERCENTAGE POINTS → bucket. ≤−15 collapsed | (−15,−5) slipped | [−5,5] held |
 *  (5,15] improved | >15 surged. */
export function deltaBucketOf(deltaPts) {
    if (deltaPts <= -15)
        return 'collapsed';
    if (deltaPts < -5)
        return 'slipped';
    if (deltaPts <= 5)
        return 'held';
    if (deltaPts <= 15)
        return 'improved';
    return 'surged';
}
/** stack-to-pot ratio → bucket. <1 under1 | 1–3 1to3 | 3–6 3to6 | 6–13 6to13 | >13 over13. */
export function sprBucketOf(spr) {
    if (spr < 1)
        return 'under1';
    if (spr < 3)
        return '1to3';
    if (spr < 6)
        return '3to6';
    if (spr <= 13)
        return '6to13';
    return 'over13';
}
/** absolute EV gap in bb → bucket. <0.25 negligible | <1 small | <3 clear | ≥3 severe. */
export function evGapBucketOf(evBB) {
    const a = Math.abs(evBB);
    if (a < 0.25)
        return 'negligible';
    if (a < 1)
        return 'small';
    if (a < 3)
        return 'clear';
    return 'severe';
}
// ── firewall guard (enforced, ENGINE_DESIGN.md §10.5 digest-safety) ──────────────
/** A raw-card token - a rank glyph immediately followed by a suit glyph (ARCHITECTURE.md §5 rule 2,
 *  ENGINE_DESIGN.md §10.5). Case-sensitive on the suit (lowercase c/d/h/s), so it catches "As"/"Td"/
 *  "9c" but not the word "as".
 *
 *  BOUNDARY-AWARE ON BOTH SIDES: a card reference is an ISOLATED two-character token - rank+suit that
 *  stands alone, delimited before AND after. The naive `/[2-9TJQKA][cdhs]/` from §10.5 is too eager in
 *  two directions:
 *    • LEADING guard `(?:^|[^A-Za-z])`: the rank+suit must not be the tail of a camelCase word. The
 *      legitimate sizing-intent enum `twoThirds` contains "Th" mid-word, and the digest MUST carry a
 *      `twoThirds` recommendation - the leading non-letter requirement drops it.
 *    • TRAILING guard `(?![A-Za-z])`: the rank+suit must not be the HEAD of a longer word. Authored
 *      coach prose is full of ordinary words that open with a rank glyph + a lowercase suit glyph -
 *      "The", "There", "This", "Ahead", "Ace", "Action" - and every one of them continues into more
 *      letters. Requiring a non-letter (or end-of-string) after the suit drops all of them.
 *  A REAL card leak - an authored "Ah-high" label, a card list `[As,Ks]`, a bare "Td", "the Th," -
 *  is always an isolated token: it ends at a quote, bracket, comma, dash, space, or string end. So the
 *  two-sided boundary keeps the firewall's full teeth on genuine card references while dropping the
 *  camelCase-enum and English-word false positives. The captured group (1) is the offending card token. */
export const CARD_STRING_RE = /(?:^|[^A-Za-z])([2-9TJQKA][cdhs])(?![A-Za-z])/;
/** A dollar amount - digests carry pot-fraction intents and buckets, never dollars. */
export const DOLLAR_RE = /\$/;
export class DigestSafetyError extends Error {
    match;
    path;
    value;
    constructor(message, match, 
    /** Dotted path to the offending field in the digest (e.g. `recommendation.firedRules[2].principle`),
     *  and the full string value that carried the token. Undefined when the scan can locate the token in
     *  the serialization but not in any single string field (should not happen for authored labels). */
    path, value) {
        super(message);
        this.match = match;
        this.path = path;
        this.value = value;
        this.name = 'DigestSafetyError';
    }
}
/** Walk `node` depth-first and return the dotted path + value of the FIRST string leaf whose content
 *  matches `re`. Object keys are never tested (only values can be authored leaks), so a card-like key
 *  could not false-trigger even if one existed. Used only on the slow path (a violation is already
 *  known to exist from the serialization scan) to make the error actionable. */
function locateOffendingField(node, re, path = '') {
    if (typeof node === 'string') {
        return re.test(node) ? { path: path || '(root)', value: node } : null;
    }
    if (Array.isArray(node)) {
        for (let i = 0; i < node.length; i++) {
            const hit = locateOffendingField(node[i], re, `${path}[${i}]`);
            if (hit)
                return hit;
        }
        return null;
    }
    if (node && typeof node === 'object') {
        for (const [key, child] of Object.entries(node)) {
            const hit = locateOffendingField(child, re, path ? `${path}.${key}` : key);
            if (hit)
                return hit;
        }
    }
    return null;
}
/** Throw if the digest's canonical serialization contains a raw card string or a `$` amount. The
 *  digest is safe by construction for every structured field; this bites only on authored free-form
 *  labels that accidentally embed card-like text. Called by projectDigest; also usable as a standalone
 *  invariant check on any FactsDigest before it crosses to the coach.
 *
 *  On a violation, walks the digest to name the exact offending field (path + value) so the leak is
 *  fixable at its source instead of leaving the caller to grep for a two-character token. */
export function assertDigestSafe(digest) {
    assertSerializedSafe(digest, 'FactsDigest');
}
/** The generic firewall scan behind assertDigestSafe (and the preflop digest's assertPreflopDigestSafe):
 *  serialize `value` and throw a DigestSafetyError if it contains a raw card token or a `$` amount. Kept
 *  digest-shape-agnostic so BOTH the postflop and the boardless preflop projections share ONE guard - a
 *  new digest type can never silently skip the firewall. `label` names the type in the thrown message. */
export function assertSerializedSafe(value, label = 'digest') {
    const json = JSON.stringify(value);
    const card = json.match(CARD_STRING_RE);
    if (card) {
        const token = card[1]; // the rank+suit, without the leading delimiter
        const where = locateOffendingField(value, CARD_STRING_RE);
        const at = where ? ` at ${where.path} ("${where.value}")` : '';
        throw new DigestSafetyError(`${label} leaked a card string (${token})${at} - firewall violation (ARCHITECTURE.md §5).`, token, where?.path, where?.value);
    }
    const dollar = json.match(DOLLAR_RE);
    if (dollar) {
        const where = locateOffendingField(value, DOLLAR_RE);
        const at = where ? ` at ${where.path} ("${where.value}")` : '';
        throw new DigestSafetyError(`${label} leaked a \`$\` amount${at} - firewall violation (ARCHITECTURE.md §5).`, '$', where?.path, where?.value);
    }
}
// ── the projection ───────────────────────────────────────────────────────────────
/** Project the full EngineFacts to the coach-facing FactsDigest. Reads ONLY bucketed/enumerated
 *  fields - never hero.holeCards, never board.cards (there is no digest field for them). Runs
 *  assertDigestSafe before returning so a poisoned authored label cannot reach the coach. */
export function projectDigest(facts) {
    const digest = {
        v: 1,
        street: facts.street,
        context: {
            gameType: facts.context.gameType,
            mode: facts.context.mode,
            positionClass: facts.context.positionClass,
            position: facts.context.position,
            playersInHand: facts.context.playersInHand,
            icmPressure: facts.context.icmPressure,
        },
        sprBucket: facts.math.sprBucket,
        priceBucket: facts.math.potOdds !== undefined && facts.math.requiredEquityPct !== undefined
            ? { potOdds: facts.math.potOdds, requiredEquityPct: facts.math.requiredEquityPct }
            : undefined,
        hero: {
            handClass: facts.hero.handClass,
            drawFlags: facts.hero.drawFlags,
            equityBucket: facts.hero.equityBucket,
            equityDeltaBucket: facts.hero.equityDeltaBucket,
        },
        board: {
            textureClass: facts.board.textureClass,
            streetEvent: facts.board.streetEvent,
        },
        villain: {
            archetypeId: facts.villain.archetypeId,
            imbalances: facts.villain.imbalances,
            rangeShape: facts.villain.rangeShape,
        },
        heroPerceived: {
            summary: facts.heroPerceived.summary,
            capped: facts.heroPerceived.capped,
        },
        recommendation: {
            action: facts.recommendation.action,
            sizingIntent: facts.recommendation.sizingIntent,
            // Mirror of the preflop projection's filter (facts/preflopAssemble.ts): a rule the resolver refused
            // may never be handed to the coach as an explanation of an answer it argued against.
            //
            // THIS IS A NO-OP TODAY, ON PURPOSE. Nothing on the postflop path sets `suppressed` - resolve.ts's
            // `toFiredRule` does not, and only strategy/preflop.ts flags rules (via ruleShapedAnswer). But the
            // postflop resolver CAN refuse a matched rule's `action.set` the same way preflop's can (resolve.ts:
            // `if (setAction && !forbidden.has(setAction.value))`), so the hole is structurally identical and this
            // lane is the PAID one (/api/coach). The filter is placed now so that whenever postflop detection
            // lands, the projection is already safe rather than newly leaky.
            //
            // NOTE FOR THAT ITEM: the cache key hashes firedRule IDs (facts/cacheKey.ts `firedRuleIds`), so the
            // moment postflop rules actually get flagged, this filter WILL change keys and invalidate cached
            // coach prose. That is a deliberate decision for the detection item to make, not a surprise - and the
            // prose being invalidated was generated from a contradiction anyway.
            firedRules: facts.recommendation.firedRules
                .filter((r) => !r.suppressed)
                .map((r) => ({
                id: r.id,
                principle: r.principle,
                confidence: r.confidence,
            })),
            alternatives: facts.recommendation.alternatives.map((a) => ({
                action: a.action,
                evGapBucket: a.evGapBucket,
            })),
        },
        userAction: facts.userAction
            ? {
                action: facts.userAction.action,
                sizingIntent: facts.userAction.sizingIntent,
                arithmeticVerdict: facts.userAction.arithmeticVerdict,
                sizingFit: facts.userAction.sizingFit,
                gradeBand: facts.userAction.gradeBand,
            }
            : undefined,
    };
    // Validate the projection against the schema (catches any drift), then enforce the firewall.
    const parsed = FactsDigestSchema.parse(digest);
    assertDigestSafe(parsed);
    return parsed;
}
/** The price statement for a call: pot-odds string ("2.8:1") + required equity %. Absent when there
 *  is nothing to call (toCall 0). Lives here because both EngineFacts.math and the digest priceBucket
 *  need the same computation. */
export function priceOf(pot, toCall) {
    if (toCall <= 0)
        return undefined;
    const ratio = pot / toCall;
    const requiredEquityPct = (toCall / (pot + toCall)) * 100;
    return {
        potOdds: `${ratio.toFixed(1)}:1`,
        requiredEquityPct: Math.round(requiredEquityPct * 10) / 10,
    };
}
//# sourceMappingURL=digest.js.map